import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiClock, FiRepeat, FiArrowRight } from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';

const ConversionHistory = ({ recentConversions, onRepeatConversion }) => {
  const [selectedConversion, setSelectedConversion] = useState(null);
  const modalRef = useRef(null);

  // Focus trap and ESC to close
  useEffect(() => {
    if (!selectedConversion) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedConversion(null);
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll('button, [tabindex]:not([tabindex="-1"])');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedConversion]);

  // Copy to clipboard feedback
  const [copied, setCopied] = useState("");
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 1200);
  };

  return (
    <>
      <div className="space-y-3">
        {recentConversions.map((conversion, index) => (
          <motion.div
            key={conversion.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`group relative bg-white dark:bg-gray-900/80 
                       rounded-lg p-3.5 border border-gray-100 dark:border-gray-800/60
                       hover:border-primary-400 dark:hover:border-primary-500
                       hover:shadow-md dark:shadow-lg dark:hover:shadow-xl transition-all duration-200
                       aria-selected:border-transparent aria-selected:bg-white dark:aria-selected:bg-gray-900/80`}
            tabIndex={0}
            role="listitem"
            onClick={() => setSelectedConversion(conversion)}
            style={{ cursor: 'pointer' }}
            aria-selected={selectedConversion && selectedConversion.id === conversion.id}
          >
            {/* Category Badge and Timestamp */}
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full
                ${getCategoryBadgeClass(conversion.category)}
              `}
              tabIndex={0}
              >
                {formatCategory(conversion.category)}
              </span>
              <span className="text-[11px] text-gray-400 dark:text-neutral-400">
                {formatDistanceToNow(new Date(conversion.timestamp), { addSuffix: true })}
              </span>
            </div>
            {/* Two-line Conversion Values, side-by-side layout */}
            <div className="flex items-center min-w-0 gap-2 justify-between">
              {/* From value/unit stack */}
              <div className="flex flex-col items-start min-w-0 max-w-[90px] md:max-w-[120px] lg:max-w-[140px]">
                <span className="font-semibold text-gray-900 dark:text-neutral-100 text-[15px] truncate fade-right w-full" title={conversion.inputValue}>{conversion.inputValue}</span>
                <span className="text-[11px] text-gray-500 dark:text-neutral-400 font-medium leading-tight break-words" style={{fontSize: 'clamp(10px, 2vw, 13px)', letterSpacing: '-0.01em', wordBreak: 'break-word'}}>{conversion.from}</span>
              </div>
              {/* Arrow */}
              <FiArrowRight className="text-gray-400 dark:text-primary-400 flex-shrink-0 mx-2" />
              {/* To value/unit stack */}
              <div className="flex flex-col items-end min-w-0 max-w-[90px] md:max-w-[120px] lg:max-w-[140px]">
                <span className="font-semibold text-gray-900 dark:text-neutral-100 text-[15px] truncate fade-right w-full text-right" title={conversion.outputValue}>{conversion.outputValue}</span>
                <span className="text-[11px] text-gray-500 dark:text-neutral-400 font-medium leading-tight break-words text-right" style={{fontSize: 'clamp(10px, 2vw, 13px)', letterSpacing: '-0.01em', wordBreak: 'break-word'}}>{conversion.to}</span>
              </div>
            </div>
            {/* Repeat Button */}
            <button
              onClick={e => { e.stopPropagation(); onRepeatConversion(conversion); }}
              className="absolute right-2 top-2 p-1.5 rounded-full
                         opacity-0 group-hover:opacity-100
                         bg-gray-50 dark:bg-gray-800/60
                         hover:bg-primary-50 dark:hover:bg-primary-900/40
                         text-gray-400 dark:text-primary-400
                         hover:text-primary-600 dark:hover:text-primary-300
                         dark:shadow-md
                         transition-all duration-200"
              title="Repeat this conversion"
              tabIndex={0}
              aria-label="Repeat conversion"
            >
              <FiRepeat className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        ))}
      </div>
      {/* Modal/Side Panel for full conversion details */}
      {selectedConversion && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={() => setSelectedConversion(null)}
        >
          <div
            ref={modalRef}
            className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-primary-100 dark:border-primary-900 rounded-2xl shadow-2xl p-8 max-w-lg w-full relative outline-none"
            onClick={e => e.stopPropagation()}
            tabIndex={-1}
            aria-modal="true"
            role="dialog"
          >
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 text-xl w-8 h-8 flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-400"
              onClick={() => setSelectedConversion(null)}
              aria-label="Close"
              style={{ background: 'none', border: 'none', boxShadow: 'none' }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/></svg>
            </button>
            <div className="mb-4 flex items-center gap-3">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryBadgeClass(selectedConversion.category)}`}>{formatCategory(selectedConversion.category)}</span>
              <span className="text-[11px] text-gray-400 dark:text-neutral-400">{formatDistanceToNow(new Date(selectedConversion.timestamp), { addSuffix: true })}</span>
            </div>
            <hr className="my-4 border-gray-200 dark:border-gray-800" />
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4 text-lg font-semibold justify-between">
                {/* From value/unit stack */}
                <div className="flex flex-col items-start min-w-0 max-w-[220px]">
                  <span className="font-semibold text-gray-900 dark:text-neutral-100 text-[18px] truncate fade-right w-full" title={selectedConversion.inputValue}>{selectedConversion.inputValue}</span>
                  <span className="text-[12px] text-gray-500 dark:text-neutral-400 font-medium leading-tight break-words" style={{fontSize: 'clamp(10px, 2vw, 14px)', letterSpacing: '-0.01em', wordBreak: 'break-word'}}>{selectedConversion.from}</span>
                </div>
                {/* Arrow */}
                <FiArrowRight className="text-primary-400 flex-shrink-0 mx-2" />
                {/* To value/unit stack */}
                <div className="flex flex-col items-end min-w-0 max-w-[220px]">
                  <span className="font-semibold text-gray-900 dark:text-neutral-100 text-[18px] truncate fade-right w-full text-right" title={selectedConversion.outputValue}>{selectedConversion.outputValue}</span>
                  <span className="text-[12px] text-gray-500 dark:text-neutral-400 font-medium leading-tight break-words text-right" style={{fontSize: 'clamp(10px, 2vw, 14px)', letterSpacing: '-0.01em', wordBreak: 'break-word'}}>{selectedConversion.to}</span>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white font-medium shadow hover:bg-primary-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  onClick={() => { onRepeatConversion(selectedConversion); setSelectedConversion(null); }}
                  aria-label="Repeat this conversion"
                >
                  Repeat
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

// Helper functions
const getCategoryColor = (category) => {
  const colors = {
    number: 'emerald',
    length: 'blue',
    weight: 'purple',
    temperature: 'orange',
    'electric-charge': 'yellow',
    power: 'red',
    voltage: 'amber',
    energy: 'green',
    frequency: 'indigo',
    color: 'rose',
    resistance: 'cyan',
    image: 'teal'
  };
  return colors[category] || 'gray';
};

const getCategoryBadgeClass = (category) => {
  switch (category) {
    case 'number':
      return 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400';
    case 'temperature':
      return 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400';
    case 'length':
      return 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
    case 'weight':
      return 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400';
    case 'electric-charge':
      return 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'power':
      return 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400';
    case 'voltage':
      return 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400';
    case 'energy':
      return 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400';
    case 'frequency':
      return 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400';
    case 'color':
      return 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400';
    case 'resistance':
      return 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400';
    case 'image':
      return 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400';
    default:
      return 'bg-gray-50 text-gray-600 dark:bg-gray-900/30 dark:text-gray-300';
  }
};

const formatCategory = (category) => {
  return category
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default ConversionHistory; 